/**
 * Utility functions for localStorage management
 */

import * as Sentry from "@sentry/react";

// ── Safe storage: localStorage → sessionStorage → in-memory ────────────────
// Trust Wallet's in-app WebView (v26+) can restrict or silently fail on
// localStorage. This wrapper tries each tier in order so auth tokens are
// always stored somewhere for the current session.
const _memoryStore: Record<string, string> = {};

type StorageTier = "localStorage" | "sessionStorage" | "memory";

function _captureStorageError(
  err: unknown,
  tier: StorageTier,
  op: "setItem" | "getItem" | "removeItem" | "clear" | "probe",
  key?: string
): void {
  Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
    tags: {
      section: "safe-storage",
      storage_tier: tier,
      storage_op: op,
    },
    extra: { key: key ?? null },
  });
}

function _isStorageAvailable(
  storage: Storage,
  tier: StorageTier
): boolean {
  try {
    const probe = "__storage_probe__";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return true;
  } catch (err) {
    _captureStorageError(err, tier, "probe");
    return false;
  }
}

export const safeStorage = {
  setItem(key: string, value: string): void {
    try {
      if (_isStorageAvailable(localStorage, "localStorage")) {
        localStorage.setItem(key, value);
        return;
      }
    } catch (err) {
      _captureStorageError(err, "localStorage", "setItem", key);
    }
    try {
      if (_isStorageAvailable(sessionStorage, "sessionStorage")) {
        sessionStorage.setItem(key, value);
        return;
      }
    } catch (err) {
      _captureStorageError(err, "sessionStorage", "setItem", key);
    }
    _memoryStore[key] = value;
  },

  getItem(key: string): string | null {
    try {
      const v = localStorage.getItem(key);
      if (v !== null) return v;
    } catch (err) {
      _captureStorageError(err, "localStorage", "getItem", key);
    }
    try {
      const v = sessionStorage.getItem(key);
      if (v !== null) return v;
    } catch (err) {
      _captureStorageError(err, "sessionStorage", "getItem", key);
    }
    return _memoryStore[key] ?? null;
  },

  removeItem(key: string): void {
    try { localStorage.removeItem(key); } catch (err) {
      _captureStorageError(err, "localStorage", "removeItem", key);
    }
    try { sessionStorage.removeItem(key); } catch (err) {
      _captureStorageError(err, "sessionStorage", "removeItem", key);
    }
    delete _memoryStore[key];
  },

  clear(): void {
    try { localStorage.clear(); } catch (err) {
      _captureStorageError(err, "localStorage", "clear");
    }
    try { sessionStorage.clear(); } catch (err) {
      _captureStorageError(err, "sessionStorage", "clear");
    }
    Object.keys(_memoryStore).forEach((k) => delete _memoryStore[k]);
  },
};
// ───────────────────────────────────────────────────────────────────────────

const REFERRAL_STORAGE_KEY = 'referralAddress';

/**
 * Clear localStorage while preserving referral address
 * This should be used instead of localStorage.clear() to maintain referral functionality
 */
export const clearLocalStoragePreservingReferral = (): void => {
  const referralAddress = safeStorage.getItem(REFERRAL_STORAGE_KEY);
  safeStorage.clear();
  if (referralAddress) {
    safeStorage.setItem(REFERRAL_STORAGE_KEY, referralAddress);
  }
};

/**
 * Get referral address from localStorage
 */
export const getReferralAddress = (): string | null => {
  return safeStorage.getItem(REFERRAL_STORAGE_KEY);
};

/**
 * Set referral address in localStorage
 */
export const setReferralAddress = (address: string): void => {
  if (address) {
    safeStorage.setItem(REFERRAL_STORAGE_KEY, address.toLowerCase());
  }
};

/**
 * Clear referral address from localStorage
 */
export const clearReferralAddress = (): void => {
  safeStorage.removeItem(REFERRAL_STORAGE_KEY);
};
